import React, { useCallback, useMemo } from 'react';
import {
  OfferType,
  useCurrentOrganizationId,
  useOffers,
  usePlanOffers,
  useSendAction,
  useSession,
  useAddOfferToPlan,
  Offer,
  usePlan,
  useRecruitment,
} from '@app/api';
import { CenteredModal } from '@app/core';
import { OfferList } from '../Offers';
import { filterOffers } from 'pages/StudySetup/GeneralSetup/filterOffers';
import { useTranslation } from 'react-i18next';

interface Props {
  isOpen: boolean;
  title?: string;
  sessionId?: string;
  onRequestClose: () => void;
}

export const UpsellTargetingModal = ({
  isOpen,
  title,
  onRequestClose,
  sessionId,
}: Props) => {
  const organizationId = useCurrentOrganizationId(true);
  const { t } = useTranslation();

  const { data: allOffers } = useOffers();
  const { data: session } = useSession(sessionId);
  const { data: recruitment } = useRecruitment(session?.recruitmentId);
  const { data: planOffers } = usePlanOffers(session?.planId);
  const { data: plan } = usePlan(session?.planId);
  const { mutateAsync: updateOffer } = useAddOfferToPlan(session?.planId);
  const { mutate: sendAction } = useSendAction('unlock_targeting_feature');
  const duration = session?.duration || 60;

  const availableOffers = useMemo(() => {
    if (!session || !allOffers) return [];
    return filterOffers(
      {
        ...session,
        priceThierId: plan?.priceThierId,
      },
      allOffers,
    );
  }, [session, allOffers]);

  const targetingOffers = useMemo(
    () =>
      availableOffers?.filter(
        (offer) =>
          offer.type === OfferType.TARGETING &&
          offer.products.find((p) => !p.forRecruitmentByClient),
      ),
    [availableOffers],
  );

  const selectedOffer = useMemo(() => {
    return planOffers?.find((o) => o.type === OfferType.TARGETING);
  }, [planOffers]);

  const onSubmit = useCallback(
    async (offer: Offer) => {
      await updateOffer({
        offerId: offer.id,
      });
      sendAction({
        offer,
        session,
      });
      onRequestClose();
    },
    [onRequestClose, updateOffer, sendAction, session],
  );

  return (
    <CenteredModal
      title={title || t('editSession.upsell.targeting')}
      isOpen={isOpen && !!organizationId}
      onRequestClose={onRequestClose}
      xlarge
    >
      <OfferList
        offers={targetingOffers}
        duration={duration}
        onClick={onSubmit}
        disableDownsell
        selectedId={selectedOffer?.id}
        hideCriteriaDetails
        countryKeys={recruitment?.countryKeys}
      />
    </CenteredModal>
  );
};
